import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { lighten } from 'polished';
import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';
import { IconButton } from '../buttons';

type Props = {
  align: 'center' | 'left' | 'right',
  children: React.Node,
  className?: string,
  ariaHidden?: boolean,
  ariaLive?: 'polite' | 'off',
  mode: 'default',
  size: 'small' | 'large',
  type: 'success' | 'danger' | 'info' | 'warning',
  fontWeight: 400 | 500 | 700,
  fitContent?: boolean,
  onClose?: Function,
};

const styles = {
  large: {
    padding: '24px',
  },
  small: {
    padding: '8px 16px',
  },
};

export const Alert = withSpacingStyles(
  forwardRef(
    (
      {
        align,
        ariaHidden,
        ariaLive = 'polite',
        children,
        className,
        fitContent = false,
        fontWeight,
        mode,
        onClose,
        size = 'small',
        type,
        ...rest
      }: Props,
      ref
    ) => {
      const componentTheme = useComponentTheme(theme => theme.components.Alert, mode);

      return (
        <AlertContainer
          align={align}
          aria-hidden={ariaHidden}
          // Use `polite` instead of `assertive`, because sometimes `assertive` gets ignored completely by screen readers,
          // e.g. on VoiceOver on a Mac, if the alert happens soon after focussing another element, then it won't get
          // read.
          aria-live={ariaLive}
          className={children ? className : 'hide-text'}
          componentTheme={componentTheme}
          data-test="alert"
          ref={ref}
          size={size}
          type={type}
          fontWeight={fontWeight}
          fitContent={fitContent}
          {...rest}
        >
          {children}
          {onClose && <IconButtonWrapper icon="Times" mode="ghost" size="xsmall" onClick={onClose} />}
        </AlertContainer>
      );
    }
  )
);

Alert.displayName = 'Alert';

const AlertContainer = styled('div', {
  shouldForwardProp: prop => !['align', 'componentTheme', 'fitContent', 'fontWeight', 'size', 'type'].includes(prop),
})(
  ({ align, componentTheme, fitContent, fontWeight, size, theme, type }) => `
    padding: ${styles[size].padding};
    font-size: ${componentTheme.fontSize[size]};
    ${align ? `text-align: ${align};` : ''}
    color: ${theme.color.black};
    background-color: ${componentTheme[type] || lighten(0.3, theme.color[type]) || theme.color.success};
    border-radius: ${theme.borderRadius.small};
    ${fontWeight ? `font-weight: ${fontWeight};` : ''}
    position: relative;
    ${fitContent ? 'width: fit-content;' : 'width: 100%;'}

    @media (${theme.breakpoint.kiosk}) {
      padding: 24px;
    }
  `
);

const IconButtonWrapper = styled(IconButton)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;
